/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import MediaQuery from 'react-responsive';

import { Switch, Route } from 'react-router';

import Tabs from '../../components/Tabs';
import styles from '../../styles';
import Error from '../../components/Error';
import ErrorWithOptions from '../../components/ErrorWithOptions';

import InstanceSubscribeModalContainer from './components/InstanceSubscribeModalContainer';
import Banner from './components/Banner.js';
import DetailsHeader from './components/DetailsHeader';
import ServiceStatus from './components/ServiceStatus';

import InstanceDetailsTimeline from '../InstanceDetailsTimeline';
import InstanceDetailsMaintenances from '../InstanceDetailsMaintenances';
import LoadingSpinner from '../../components/Spinners/LoadingSpinner.js';
import Localized from '../../../shared/components/Localized';

//V2 Components
import InstanceInfo from '../../../../components/InstanceInfo';

const Wrapper = styled.div`
  width: 100%;

  @media (min-width: 768px) {
    padding: 0 0.75rem;
  }
`;

const Contents = styled.div`
  display: flex;
  margin-bottom: 1rem;

  @media (max-width: 767px) {
    height: 100%;
  }
`;

const MainColumn = styled.div`
  flex: 1 1 100%;


  @media (min-width: 768px) {
    margin-right: 0.75rem;
  }
`;

const ServiceStatusCard = styled.div`
  ${styles.borders.card}
  padding: 1rem;
  margin-bottom: 0.75rem;
`;

const ServiceStatusHeader = styled.div`
  ${styles.fonts.headingSmall}
  padding-bottom: 1rem;
`;

const ServiceStatusMobileWrapper = styled.div`
  padding: 1rem 1.25rem;
`;

const TabsCard = styled.div`
  ${styles.borders.card}
  padding-bottom: 1rem;

  @media (max-width: 767px) {
    margin-left: 0;
    margin-right: 0;
    border-radius: 0;
  }
`;

const TabsContainer = styled.div`
  height: 3rem;
  width: 100%;
`;

const SideColumn = styled.div`
  flex: 0 0 auto;
  width: 14rem;
  ${styles.borders.card}
`;

class InstanceDetails extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      isSubscribed: false,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevProps.requests.instancePreview.isCompleted && this.props.requests.instancePreview.isCompleted) {
      const _key = this.props.match.params.instanceKey ? this.props.match.params.instanceKey // is alias
        : this.props.requests.instancePreview.value.key;
      this.props.setInstance(_key);
    }
    const checkIfSubscribed = (us, ik) => {
      const isTrueOrFalse = (element) => element.instanceKey === ik;
      return this.setState({isSubscribed: us.some(isTrueOrFalse)});
    }
    if (prevProps.requests !== this.props.requests && this.props.requests.subscriber.value !== undefined) {
      const userSubscriptions = this.props.requests.subscriber.value.Subscriptions;
      const instanceKey =
        this.props.match.params.hasOwnProperty('aliasKey') &&
          this.props.requests.instancePreview.value !== undefined
          ? this.props.requests.instancePreview.value.key
          : this.props.match.params.instanceKey;
      checkIfSubscribed(userSubscriptions, instanceKey);
    }
    if (this.props.requests.subscriber.value === undefined && prevProps.requests.subscriber.value !== undefined) {
      const userSubscriptions = prevProps.requests.subscriber.value.Subscriptions;
      const instanceKey =
        prevProps.match.params.hasOwnProperty('aliasKey') &&
        prevProps.requests.instancePreview.value !== undefined
          ? prevProps.requests.instancePreview.value.key
          : this.props.match.params.instanceKey;
      checkIfSubscribed(userSubscriptions, instanceKey);
    }
  }

  render() {
    const { requests, match, localePicker, location } = this.props;
    const { isCompleted, isErrored } = requests.instancePreview;
    const instancePreview = requests.instancePreview.value;
    const isActive = typeof instancePreview !== 'undefined' ? instancePreview.isActive : false;
    const isAnInstanceAlias = match.params.hasOwnProperty('aliasKey');
    const aliasKey = isAnInstanceAlias && match.params.aliasKey;
    const queryParam = location.search || '';

    // If it is an instanceAlias URL and no alias was found for this URL, render Error page with ability to search
    if (isAnInstanceAlias && isErrored) {
      return (
        <ErrorWithOptions
          targetUrl={`/search/${aliasKey}`}
          messageModelKey="instanceAlias"
          buttonText={`Search for ${aliasKey}`}
        />
      );
    }

    // If it is an Instance and the instance is not active or the Instance does not exsist return the 404 Page
    if ((isCompleted && !isActive) || (!isAnInstanceAlias && isErrored)) return <Error />;

    // Show a loading indicator while the request is being processed
    if (!isCompleted) return <LoadingSpinner inverse />;

    const tabs = [
      {
        name: <Localized modelKey='currentStatus' modelAttribute='label' modelName='tabs' />,
        url: match.url + queryParam,
      }, {
        name: <Localized modelKey='history' modelAttribute='label' modelName='tabs' />,
        url: `${match.url}/history/${queryParam}`,
      }, {
        name: <Localized modelKey='maintenances' modelAttribute='label' modelName='tabs' />,
        url: `${match.url}/maintenances/${queryParam}`,
      }
    ];

    const mobileTabs = [
      {
        name: <Localized modelKey='status' modelAttribute='label' modelName='tabs' />,
        url: match.url + queryParam,
      }, {
        name: <Localized modelKey='history' modelAttribute='label' modelName='tabs' />,
        url: `${match.url}/history/${queryParam}`,
      }, {
        name: <Localized modelKey='maintenances' modelAttribute='label' modelName='tabs' />,
        url: `${match.url}/maintenances/${queryParam}`,
      }, {
        name: <Localized modelKey='details' modelAttribute='label' modelName='tabs' />,
        url: `${match.url}/details/${queryParam}`,
      }
    ];

    // Using MediaQuery from react-responsive package for handling responsive layout change
    // CSS-only media queries will always render components, then show/hide via CSS
    // But the MediaQuery component coniditionally renders components

    // for maintenanceList, need define the instance key
    const _key = match.params.instanceKey ? match.params.instanceKey // is alias
      : instancePreview.key;

    return (
      <Wrapper>
        <Banner
          instance={instancePreview}
          instanceAlias={match.params.aliasKey}
        >
          <DetailsHeader
            instanceKey={instancePreview.key}
            instanceAlias={match.params.aliasKey}
            locale={localePicker}
            isSubscribed={this.state.isSubscribed}
          />
        </Banner>
        <Contents>
          <MainColumn>
            <TabsCard>
              <MediaQuery maxWidth={767}>
                {(matchs) => {
                  return (
                    <TabsContainer>
                      <Tabs items={matchs ? mobileTabs : tabs} />
                    </TabsContainer>
                  );
                }}
              </MediaQuery>
              <MediaQuery maxWidth={767}>
                <Switch>
                  <Route exact path={`${match.path}`}>
                    <ServiceStatusMobileWrapper>
                      <ServiceStatus instance={instancePreview} />
                    </ServiceStatusMobileWrapper>
                  </Route>
                  <Route exact path={`${match.path}/subscribe`}>
                    <ServiceStatusMobileWrapper>
                      <InstanceSubscribeModalContainer />
                      <ServiceStatus instance={instancePreview} />
                    </ServiceStatusMobileWrapper>
                  </Route>
                  <Route exact path={`${match.path}/maintenances`}>
                    <InstanceDetailsMaintenances instanceKey={_key} />
                  </Route>
                  <Route path={`${match.path}/history`}>
                    <InstanceDetailsTimeline
                      queryParam={queryParam}
                      instanceKey={match.params.instanceKey}
                      aliasKey={match.params.aliasKey} />
                  </Route>
                  <Route exact path={`${match.path}/details`}>
                    <InstanceInfo instance={instancePreview} />
                  </Route>
                </Switch>
              </MediaQuery>
              <MediaQuery minWidth={768}>
                <Switch>
                  <Route exact path={match.path + '/subscribe'}>
                    <ServiceStatusCard>
                      <InstanceSubscribeModalContainer />
                      <ServiceStatusHeader>
                        <Localized modelKey='currentStatusByService'
                          modelAttribute='label' modelName='tabs' />
                      </ServiceStatusHeader>
                      <ServiceStatus instance={instancePreview} />
                    </ServiceStatusCard>
                  </Route>
                  <Route exact path={match.path}>
                    <ServiceStatusCard>
                      <ServiceStatusHeader>
                        <Localized modelKey='currentStatusByService'
                          modelAttribute='label' modelName='tabs' />
                      </ServiceStatusHeader>
                      <ServiceStatus instance={instancePreview} />
                    </ServiceStatusCard>
                  </Route>
                  <Route exact path={`${match.path}/(maintenances)?`}>
                    <InstanceDetailsMaintenances instanceKey={_key} />
                  </Route>
                  <Route path={`${match.path}/history`}>
                    <InstanceDetailsTimeline
                      queryParam={queryParam}
                      instanceKey={match.params.instanceKey}
                      aliasKey={match.params.aliasKey} />
                  </Route>
                </Switch>
              </MediaQuery>
            </TabsCard>
          </MainColumn>
          <MediaQuery minWidth={768}>
            <SideColumn>
              <InstanceInfo instance={instancePreview} />
            </SideColumn>
          </MediaQuery>
        </Contents>
      </Wrapper>
    );
  }
}

InstanceDetails.propTypes = {
  requests: PropTypes.shape({
    instancePreview: PropTypes.object,
    subscriber: PropTypes.object
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      instanceKey: PropTypes.string
    })
  })
};

export default InstanceDetails;
